import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { MapPin, Layers, Tag, Navigation, ExternalLink, CreditCard } from 'lucide-react';
import { Button } from './ui/button';

const getBarColor = (pct) => {
  if (pct >= 90) return 'bg-red-500';
  if (pct >= 70) return 'bg-yellow-500';
  return 'bg-green-500';
};

const getBarTextColor = (pct) => {
  if (pct >= 90) return 'text-red-600';
  if (pct >= 70) return 'text-yellow-600';
  return 'text-green-600';
};

const AvailabilityBar = ({ label, available, total, utilizedPercent }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="flex items-center space-x-2">
        <span className={`text-sm font-bold ${getBarTextColor(utilizedPercent)}`}>{available}</span>
        <span className="text-xs text-slate-400">/ {total}</span>
        <span className="text-xs text-slate-500 w-10 text-right">{utilizedPercent}%</span>
      </div>
    </div>
    <div className="w-full bg-slate-100 rounded-full h-2">
      <div className={`h-2 rounded-full transition-all ${getBarColor(utilizedPercent)}`} style={{ width: `${utilizedPercent}%` }} />
    </div>
  </div>
);

const LocationDetailModal = ({ garage, open, onClose, primaryColor, onReserveClick }) => {
  if (!garage) return null;

  const brandColor = primaryColor || '#3b82f6';
  const isGarage = garage.locationType === 'garage';
  const levels = garage.levels || [];
  const attributes = garage.attributes || [];

  const getDirectionsUrl = () => {
    const coords = garage.coordinates;
    if (coords) {
      const lat = Array.isArray(coords) ? coords[0] : coords.lat;
      const lng = Array.isArray(coords) ? coords[1] : coords.lng;
      if (lat && lng) return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(garage.address || '')}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-800">{garage.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {/* Photo */}
          {garage.photoUrl && (
            <div className="rounded-xl overflow-hidden border border-slate-200">
              <img src={garage.photoUrl} alt={garage.name} className="w-full h-48 object-cover" />
            </div>
          )}

          {/* Info Row */}
          <div className="flex items-center flex-wrap gap-2">
            <div className="flex items-center text-sm text-slate-600">
              <MapPin className="w-4 h-4 mr-1" />{garage.address}
            </div>
            <Badge className="bg-slate-100 text-slate-600 text-xs">
              {isGarage ? `Garage · ${levels.length} levels` : 'Surface Lot'}
            </Badge>
            {(garage.tags || []).map(tag => (
              <Badge key={tag} className="bg-blue-50 text-blue-600 text-xs">{tag}</Badge>
            ))}
          </div>

          {/* Overall Summary */}
          <div className="bg-slate-50 rounded-xl p-5">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold text-green-600">{garage.availableSpaces}</div>
                <div className="text-sm text-slate-500">Available</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-slate-800">{garage.totalSpaces}</div>
                <div className="text-sm text-slate-500">Total</div>
              </div>
              <div>
                <div className={`text-3xl font-bold ${getBarTextColor(garage.utilizedPercent)}`}>{garage.utilizedPercent}%</div>
                <div className="text-sm text-slate-500">Utilized</div>
              </div>
            </div>
            <div className="mt-3 w-full bg-slate-200 rounded-full h-3">
              <div className={`h-3 rounded-full transition-all ${getBarColor(garage.utilizedPercent)}`} style={{ width: `${garage.utilizedPercent}%` }} />
            </div>
          </div>

          {/* Levels (Garages only) */}
          {isGarage && levels.length > 0 && (
            <div>
              <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center space-x-2">
                <Layers className="w-4 h-4" /><span>Availability by Level</span>
              </h3>
              <div className="space-y-3 bg-white rounded-xl border border-slate-200 p-4">
                {levels.map((level, i) => (
                  <AvailabilityBar key={i} label={level.name} available={level.availableSpaces} total={level.totalSpaces} utilizedPercent={level.utilizedPercent} />
                ))}
              </div>
            </div>
          )}

          {/* Attributes */}
          {attributes.length > 0 && (
            <div>
              <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center space-x-2">
                <Tag className="w-4 h-4" /><span>Availability by Type</span>
              </h3>
              <div className="space-y-3 bg-white rounded-xl border border-slate-200 p-4">
                {attributes.map((attr, i) => (
                  <AvailabilityBar key={i} label={attr.name} available={attr.availableSpaces} total={attr.totalSpaces} utilizedPercent={attr.utilizedPercent} />
                ))}
              </div>
            </div>
          )}

          {/* Reserve Button */}
          {onReserveClick && (
            <Button
              onClick={() => onReserveClick(garage)}
              variant="outline"
              className="w-full text-sm border-2 font-semibold"
              style={{ borderColor: brandColor, color: brandColor }}
              data-testid={`modal-reserve-btn-${garage.id}`}
            >
              <CreditCard className="w-4 h-4 mr-2" />Pay For Reserved Parking
            </Button>
          )}

          {/* Directions Button */}
          <Button onClick={() => window.open(getDirectionsUrl(), '_blank')} className="w-full text-white" style={{ backgroundColor: brandColor }}>
            <Navigation className="w-4 h-4 mr-2" />Get Directions<ExternalLink className="w-4 h-4 ml-2" />
          </Button>

          <div className="text-xs text-slate-500 text-center">Last updated: {garage.lastUpdated?.toLocaleString?.() || 'N/A'}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LocationDetailModal;
